import React from "react";
import cn from "classnames";
import {
  GatewayMachine,
  GatewayWithMachinesViewStateTypes,
} from "@src/types/gateways-with-machines";
import sourceIcon from "@src/assets/svg/source-icon.svg";
import treeStructureIcon from "@src/assets/svg/tree-structure-icon.svg";
import conditionMonitoringIcon from "@src/assets/svg/condition-monitoring-icon.svg";
import dashboardIcon from "@src/assets/svg/dashboard-icon.svg";
import MachineSettingsIcon from "@src/assets/svg/machine-settings-icon.svg";
import shotDataTableIcon from "@src/assets/svg/short-data-table-icon.svg";
import styles from "./gateway-machine-list-row.module.scss";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { RenderIfUserPermissionsRulesMatch } from "../render-if-user-permissions-rules-match/render-if-user-permissions-rules-match";
import { TicketSupportForMachineRowIconButton } from "@src/components/atoms/ticket-support-for-machine-row-icon-button/ticket-support-for-machine-row-icon-button";
import { RenderIfUserHasMultiplePermissions } from "../render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";
import { RenderIfUserHasPermission } from "../render-if-user-has-permission/render-if-user-has-permission";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import { EditMachineName } from "../edit-machine-name/edit-machine-name";
import { useGatewayMachineListRowHook } from "./use-gateway-machine-list-row-hook";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

interface GatewayMachineListRowProps {
  gatewayData: GatewayWithMachinesViewStateTypes;
  machine: GatewayMachine;
}

const cssClassName = "gateway-machine-list-row";

export const GatewayMachineListRow = ({
  gatewayData,
  machine,
}: GatewayMachineListRowProps) => {
  const {
    texts,
    showOTA,
    showShotDataTable,
    hasOpcUaSkill,
    actionDisabledReason,
    showActionLinks,
    isUserConditionMonitoringViewer,
    handleOpcUaActionClick,
    clickTreeStructureLink,
    clickDashboardLink,
    clickConditionMonitoringLink,
    clickMachineSettingsLink,
    clickShotDataTableLink,
  } = useGatewayMachineListRowHook({ gatewayData, machine });

  const handleCopyMachineId = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(machine.machine_id);
      successSnackbar(texts.successMessage);
    } catch {
      errorSnackbar(texts.errorMessage);
    }
  };

  return (
    <tr
      key={machine.machine_id}
      className={styles[`${cssClassName}__grid-row`]}
    >
      <td className={styles[`${cssClassName}__grid-cell`]}>
        <span className={cn(styles[`${cssClassName}__grid-cell-flex-wrapper`])}>
          <span
            className={cn(
              styles[`${cssClassName}__description-text`],
              styles[`${cssClassName}__description-with-icon`],
              styles[
                `${cssClassName}__grid-cell-flex-wrapper--machine-name-container`
              ],
            )}
          >
            {/* <IndicatorText
              text={""}
              variant={
                machine.connection_state.toLowerCase() === "connected"
                  ? "green"
                  : "orange"
              }
            /> */}
            <img src={sourceIcon} alt={texts.machineIconAltText} />
            <EditMachineName machine={machine} />
          </span>
          {showActionLinks && (
            <span
              className={cn(
                styles[
                  `${cssClassName}__grid-cell-flex-wrapper--links-outer-wrapper`
                ],
                styles[
                  `${cssClassName}__grid-cell-flex-wrapper--padding-right`
                ],
              )}
            >
              <span className={styles[`${cssClassName}__links-wrapper`]}>
                <VerticalSeparator />
                {hasOpcUaSkill && (
                  <>
                    <RenderIfUserPermissionsRulesMatch
                      mode="anyInEachGroup"
                      permissions={[
                        ["view-active-nodeset", "manage-active-nodeset"],
                        "view-initial-server-nodeset",
                      ]}
                    >
                      {!actionDisabledReason ? (
                        <IconButton
                          fun={(e) =>
                            handleOpcUaActionClick(e, clickTreeStructureLink)
                          }
                          icon={treeStructureIcon}
                          tooltip={texts.goToTreeStructure}
                        />
                      ) : (
                        <CustomTooltip
                          content={texts.machineConfigurationWarning}
                          maxWidth="360px"
                        >
                          <IconButton
                            fun={() => undefined}
                            icon={treeStructureIcon}
                            disabled
                          />
                        </CustomTooltip>
                      )}
                    </RenderIfUserPermissionsRulesMatch>
                    <RenderIfUserHasPermission permission="view-dashboards">
                      {!actionDisabledReason ? (
                        <IconButton
                          fun={(e) =>
                            handleOpcUaActionClick(e, clickDashboardLink)
                          }
                          icon={dashboardIcon}
                          tooltip={texts.goToDashboard}
                        />
                      ) : (
                        <CustomTooltip
                          content={texts.machineConfigurationWarning}
                          maxWidth="360px"
                        >
                          <IconButton
                            fun={() => undefined}
                            icon={dashboardIcon}
                            disabled
                          />
                        </CustomTooltip>
                      )}
                    </RenderIfUserHasPermission>
                    {isUserConditionMonitoringViewer() &&
                      (!actionDisabledReason ? (
                        <IconButton
                          fun={(e) =>
                            handleOpcUaActionClick(
                              e,
                              clickConditionMonitoringLink,
                            )
                          }
                          icon={conditionMonitoringIcon}
                          tooltip={texts.goToConditionMonitoring}
                        />
                      ) : (
                        <CustomTooltip
                          content={texts.machineConfigurationWarning}
                          maxWidth="360px"
                        >
                          <IconButton
                            fun={() => undefined}
                            icon={conditionMonitoringIcon}
                            disabled
                          />
                        </CustomTooltip>
                      ))}
                  </>
                )}

                <RenderIfUserHasPermission permission="create-tickets">
                  <TicketSupportForMachineRowIconButton
                    machineName={machine.name}
                    machineId={machine.machine_id}
                  />
                </RenderIfUserHasPermission>

                {showOTA && hasOpcUaSkill && (
                  <RenderIfUserHasMultiplePermissions
                    permissions={[
                      "initiate-update-process",
                      "approve-update-process",
                      "manage-firmware",
                    ]}
                  >
                    {!actionDisabledReason ? (
                      <IconButton
                        fun={(e) =>
                          handleOpcUaActionClick(e, clickMachineSettingsLink)
                        }
                        icon={MachineSettingsIcon}
                        tooltip={texts.goToMachineSettings}
                      />
                    ) : (
                      <CustomTooltip
                        content={texts.machineConfigurationWarning}
                        maxWidth="360px"
                      >
                        <IconButton
                          fun={() => undefined}
                          icon={MachineSettingsIcon}
                          disabled
                        />
                      </CustomTooltip>
                    )}
                  </RenderIfUserHasMultiplePermissions>
                )}

                {showShotDataTable && hasOpcUaSkill && (
                  <RenderIfUserHasPermission permission="view-dashboards">
                    {!actionDisabledReason ? (
                      <IconButton
                        fun={(e) =>
                          handleOpcUaActionClick(e, clickShotDataTableLink)
                        }
                        icon={shotDataTableIcon}
                        tooltip={texts.goToShotDataTable}
                      />
                    ) : (
                      <CustomTooltip
                        content={texts.machineConfigurationWarning}
                        maxWidth="360px"
                      >
                        <IconButton
                          fun={() => undefined}
                          icon={shotDataTableIcon}
                          disabled
                        />
                      </CustomTooltip>
                    )}
                  </RenderIfUserHasPermission>
                )}
              </span>
            </span>
          )}
        </span>
      </td>
      <td className={styles[`${cssClassName}__grid-cell`]}>
        {machine.machine_id ? (
          <button
            type="button"
            className={cn(
              styles[`${cssClassName}__description-text`],
              styles[`${cssClassName}__machine-id-copy-button`],
            )}
            aria-label={`${texts.copyTooltip}: ${machine.machine_id}`}
            onClick={handleCopyMachineId}
          >
            {machine.machine_id}
          </button>
        ) : (
          <span className={styles[`${cssClassName}__description-text`]}>
            {texts.noMachineId}
          </span>
        )}
      </td>
      <td className={styles[`${cssClassName}__grid-cell`]}>
        <span className={styles[`${cssClassName}__description-text`]}>
          {machine.dsn}
        </span>
      </td>
      {/* <td className={styles[`${cssClassName}__grid-cell`]}>
        <IndicatorText
          text={""}
          variant={
            item.connection_state.toLowerCase() === "connected"
              ? "green"
              : "orange"
          }
        />
        <span className={styles[`${cssClassName}__description-text`]}>
          Gateway Example where is the API
        </span>
        <img src={gatewayIcon} alt={texts.gatewayIconAltText} />
      </td> */}
    </tr>
  );
};
